import {
  OrganisationPreferenceRemoteInterface,
  OrganisationResponseInterface
} from "@careapp/angular-sdk";
import { BrandingInterface } from "@careapp/angular-sdk/src";
import { createSelector } from "reselect";

import { CAAction, Organisations } from "../actions";
import { DataListState, INITIAL_DATA_LIST_STATE } from "./data-list.state";
import { ReducerStateFactory } from "./state-factory.reducer";

export type OrganisationState = DataListState<OrganisationResponseInterface> & {
  selectable: string[];
  roles: { [orgId: string]: string[] };
  loadingBranding: boolean;
  loadingPreferences: boolean;
  loadingOwn: boolean;
  preferences: { [id: string]: OrganisationPreferenceRemoteInterface };
  branding: { [id: string]: BrandingInterface };
};

class OrganisationsState extends ReducerStateFactory<
  OrganisationResponseInterface
> {
  get initialState(): OrganisationState {
    return {
      ...INITIAL_DATA_LIST_STATE,
      loadingBranding: false,
      loadingPreferences: false,
      roles: {},
      loadingOwn: false,
      selectable: [],
      preferences: {},
      branding: {}
    };
  }

  constructor() {
    super(Organisations);
  }

  public reducer(
    state: OrganisationState = this.initialState,
    action: CAAction
  ) {
    switch (action.type) {
      case Organisations.REQUEST_OWN:
        return { ...state, loadingOwn: true };
      case Organisations.REQUEST_PREFERENCES:
      case Organisations.UPDATE_PREFERENCES:
        return { ...state, loadingPreferences: true };
      case Organisations.RESPONSE_SUCCESS_OWN:
        let newEntities = action.payload.reduce(
          (prev, next) => ({ ...prev, [next.id]: next }),
          {}
        );
        let newIds = Array.from(
          new Set<string>([...state.ids, ...Object.keys(newEntities)])
        );
        let roles = action.payload.reduce(
          (prev, next) => ({
            ...prev,
            [next.id]: next.roles
          }),
          {}
        );
        return {
          ...state,
          loadingOwn: false,
          ids: newIds,
          roles,
          entities: { ...state.entities, ...newEntities },
          selectable: Object.keys(newEntities)
        };
      case Organisations.FAIL_UPDATE_PREFERENCES: {
        return { ...state, loadingPreferences: false };
      }
      case Organisations.SUCCESS_UPDATE_PREFERENCES:
      case Organisations.RESPONSE_SUCCESS_PREFERENCES: {
        let preferences = { ...state.preferences };
        preferences[action.payload.organisationId] = action.payload;
        return { ...state, preferences, loadingPreferences: false };
      }
      case Organisations.REQUEST_BRANDING: {
        return { ...state, loadingBranding: true };
      }
      case Organisations.RESPONSE_SUCCESS_BRANDING:
      case Organisations.SUCCESS_UPDATE_BRANDING_COLORS:
      case Organisations.SUCCESS_UPDATE_BRANDING_IMAGE:
        let branding = { ...state.branding };
        branding[action.payload.organisationId] = action.payload;
        return { ...state, branding, loadingBranding: false };
      case Organisations.RESPONSE_FAIL_BRANDING:
        return { ...state, loadingBranding: false };
    }
    return super.reducer(state, action);
  }
}

export const state = new OrganisationsState();

export const createSelectors = (rootReducer: any) => ({
  getPreferencesLoading: createSelector(
    rootReducer,
    (state: OrganisationState) => state.loadingPreferences
  ),
  getSelectableIds: createSelector(
    rootReducer,
    (state: OrganisationState) => state.selectable
  ),
  getSelectable: createSelector(rootReducer, (state: OrganisationState) =>
    state.selectable
      .map(id => state.entities[id])
      .filter(org => (<any>org).enabled)
  ),
  currentOrgPreferences: createSelector(
    rootReducer,
    (state: OrganisationState) => state.preferences[state.selectedId]
  ),
  currentOrgBranding: createSelector(
    rootReducer,
    (state: OrganisationState) => state.branding[state.selectedId]
  ),
  currentOrgRoles: createSelector(rootReducer, (state: OrganisationState) => {
    return state.roles[state.selectedId] || [];
  })
});

export function reducer(...args) {
  return state.reducer.apply(state, args);
}
